import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Trash2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Filter, Search } from 'lucide-react';
import {
  useLeafletDocumentsQuery,
  useLeafletContentTypesQuery,
  useDeleteLeafletDocumentMutation,
  LeafletDocumentSummary,
} from '../../api/hooks/useLeaflet';
import LeafletChunkDetailModal from './LeafletChunkDetailModal';

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colorMap: Record<string, string> = {
    indexed: 'bg-green-100 text-green-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    processing: 'bg-yellow-100 text-yellow-800 dark:bg-amber-900/30 dark:text-amber-300',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  };
  const classes = colorMap[status.toLowerCase()] ?? 'bg-gray-100 text-gray-800 dark:bg-graphite-surface-2 dark:text-graphite-muted';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${classes}`}>
      {status}
    </span>
  );
};

const ConfirmDeleteDialog: React.FC<{
  document: LeafletDocumentSummary;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ document, error, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full dark:bg-graphite-surface">
      <h2 className="text-lg font-semibold mb-2">Smazat dokument?</h2>
      <p className="text-sm text-gray-600 mb-4 dark:text-graphite-muted">
        Opravdu chcete smazat <strong>{document.filename}</strong>? Tato akce je nevratná.
      </p>
      {error && (
        <p className="text-sm text-red-600 mb-3 dark:text-red-400">{error}</p>
      )}
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50 dark:border-graphite-border dark:hover:bg-white/5"
        >
          Zrušit
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700"
        >
          Smazat
        </button>
      </div>
    </div>
  </div>
);

interface SortableHeaderProps {
  column: string;
  sortBy: string;
  sortDescending: boolean;
  onSort: (column: string) => void;
  children: React.ReactNode;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({ column, sortBy, sortDescending, onSort, children }) => {
  const isActive = sortBy === column;
  return (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none dark:text-graphite-muted dark:hover:bg-white/5"
      onClick={() => onSort(column)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        <div className="flex flex-col">
          <ChevronUp
            className={`h-3 w-3 ${isActive && !sortDescending ? 'text-indigo-600 dark:text-graphite-accent' : 'text-gray-300 dark:text-graphite-faint'}`}
          />
          <ChevronDown
            className={`h-3 w-3 -mt-1 ${isActive && sortDescending ? 'text-indigo-600 dark:text-graphite-accent' : 'text-gray-300 dark:text-graphite-faint'}`}
          />
        </div>
      </div>
    </th>
  );
};

interface Props {
  canDelete: boolean;
}

const DEFAULT_SORT_BY = 'IngestedAt';
const DEFAULT_SORT_DESC = true;
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;

const LeafletDocumentsTab: React.FC<Props> = ({ canDelete }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialPage = () => {
    const p = searchParams.get('lfPage');
    return p ? Math.max(1, parseInt(p, 10)) : DEFAULT_PAGE;
  };
  const getInitialPageSize = () => {
    const ps = searchParams.get('lfPageSize');
    const val = ps ? parseInt(ps, 10) : DEFAULT_PAGE_SIZE;
    return [10, 20, 50].includes(val) ? val : DEFAULT_PAGE_SIZE;
  };
  const getInitialSortBy = () => searchParams.get('lfSortBy') || DEFAULT_SORT_BY;
  const getInitialSortDesc = () => searchParams.get('lfSortDesc') !== 'false';
  const getInitialFilenameFilter = () => searchParams.get('lfFilename') || '';
  const getInitialStatusFilter = () => searchParams.get('lfStatus') || '';
  const getInitialContentTypeFilter = () => searchParams.get('lfContentType') || '';

  const [pageNumber, setPageNumber] = useState(getInitialPage);
  const [pageSize, setPageSize] = useState(getInitialPageSize);
  const [sortBy, setSortBy] = useState(getInitialSortBy);
  const [sortDescending, setSortDescending] = useState(getInitialSortDesc);
  const [filenameFilter, setFilenameFilter] = useState(getInitialFilenameFilter);
  const [statusFilter, setStatusFilter] = useState(getInitialStatusFilter);
  const [contentTypeFilter, setContentTypeFilter] = useState(getInitialContentTypeFilter);
  const [filenameInput, setFilenameInput] = useState(getInitialFilenameFilter);

  const [pendingDelete, setPendingDelete] = useState<LeafletDocumentSummary | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [selectedChunkId, setSelectedChunkId] = useState<string | null>(null);

  const { data, isLoading, error } = useLeafletDocumentsQuery({
    pageNumber,
    pageSize,
    sortBy,
    sortDescending,
    filenameFilter: filenameFilter || undefined,
    statusFilter: statusFilter || undefined,
    contentTypeFilter: contentTypeFilter || undefined,
  });

  const { data: contentTypesData } = useLeafletContentTypesQuery();
  const deleteDocument = useDeleteLeafletDocumentMutation();

  // Sync state → URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    if (pageNumber !== DEFAULT_PAGE) params.set('lfPage', pageNumber.toString());
    else params.delete('lfPage');

    if (pageSize !== DEFAULT_PAGE_SIZE) params.set('lfPageSize', pageSize.toString());
    else params.delete('lfPageSize');

    if (sortBy !== DEFAULT_SORT_BY) params.set('lfSortBy', sortBy);
    else params.delete('lfSortBy');

    if (sortDescending !== DEFAULT_SORT_DESC) params.set('lfSortDesc', 'false');
    else params.delete('lfSortDesc');

    if (filenameFilter) params.set('lfFilename', filenameFilter);
    else params.delete('lfFilename');

    if (statusFilter) params.set('lfStatus', statusFilter);
    else params.delete('lfStatus');

    if (contentTypeFilter) params.set('lfContentType', contentTypeFilter);
    else params.delete('lfContentType');

    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageNumber,
    pageSize,
    sortBy,
    sortDescending,
    filenameFilter,
    statusFilter,
    contentTypeFilter,
    setSearchParams,
  ]);

  // URL → state (back/forward navigation)
  useEffect(() => {
    const newPage = Math.max(1, parseInt(searchParams.get('lfPage') || '1', 10));
    const newPageSizeRaw = parseInt(searchParams.get('lfPageSize') || '20', 10);
    const newPageSize = [10, 20, 50].includes(newPageSizeRaw) ? newPageSizeRaw : DEFAULT_PAGE_SIZE;
    const newSortBy = searchParams.get('lfSortBy') || DEFAULT_SORT_BY;
    const newSortDesc = searchParams.get('lfSortDesc') !== 'false';
    const newFilename = searchParams.get('lfFilename') || '';
    const newStatus = searchParams.get('lfStatus') || '';
    const newContentType = searchParams.get('lfContentType') || '';

    if (
      newPage !== pageNumber ||
      newPageSize !== pageSize ||
      newSortBy !== sortBy ||
      newSortDesc !== sortDescending ||
      newFilename !== filenameFilter ||
      newStatus !== statusFilter ||
      newContentType !== contentTypeFilter
    ) {
      setPageNumber(newPage);
      setPageSize(newPageSize);
      setSortBy(newSortBy);
      setSortDescending(newSortDesc);
      setFilenameFilter(newFilename);
      setFilenameInput(newFilename);
      setStatusFilter(newStatus);
      setContentTypeFilter(newContentType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleApplyFilters = () => {
    setFilenameFilter(filenameInput);
    setPageNumber(1);
  };

  const handleClearFilters = () => {
    setFilenameInput('');
    setFilenameFilter('');
    setStatusFilter('');
    setContentTypeFilter('');
    setSortBy(DEFAULT_SORT_BY);
    setSortDescending(DEFAULT_SORT_DESC);
    setPageNumber(1);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(column);
      setSortDescending(false);
    }
    setPageNumber(1);
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDelete) return;
    setDeleteError(null);
    try {
      await deleteDocument.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
    } catch {
      setDeleteError('Smazání se nezdařilo. Zkuste to znovu.');
    }
  };

  const handleDeleteCancel = () => {
    setPendingDelete(null);
    setDeleteError(null);
  };

  const totalPages = data?.totalPages ?? 0;
  const documents = data?.documents ?? [];

  if (isLoading) {
    return (
      <div className="space-y-2 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-10 bg-gray-100 rounded dark:bg-graphite-surface-2" />
        ))}
      </div>
    );
  }

  if (error) {
    return <div className="text-red-600 text-sm dark:text-red-400">Nepodařilo se načíst dokumenty.</div>;
  }

  return (
    <>
      {/* Filter bar */}
      <div className="bg-white shadow rounded-lg p-4 mb-4 dark:bg-graphite-surface dark:shadow-soft-dark">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex items-center">
              <Filter className="h-4 w-4 text-gray-400 mr-2 dark:text-graphite-faint" />
              <span className="text-sm font-medium text-gray-900 dark:text-graphite-text">Filtry:</span>
            </div>

            <div className="flex-1 max-w-xs">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400 dark:text-graphite-faint" />
                </div>
                <input
                  type="text"
                  value={filenameInput}
                  onChange={(e) => setFilenameInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                  placeholder="Název souboru..."
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-3 py-2 sm:text-sm border-gray-300 rounded-md dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text dark:placeholder-graphite-faint"
                />
              </div>
            </div>

            <div className="flex-1 max-w-xs">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPageNumber(1);
                }}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text"
              >
                <option value="">Vše (stav)</option>
                <option value="indexed">Indexováno</option>
                <option value="processing">Zpracovává se</option>
                <option value="failed">Selhalo</option>
              </select>
            </div>

            <div className="flex-1 max-w-xs">
              <select
                value={contentTypeFilter}
                onChange={(e) => {
                  setContentTypeFilter(e.target.value);
                  setPageNumber(1);
                }}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text"
              >
                <option value="">Vše (typ)</option>
                {(contentTypesData?.contentTypes ?? []).map((ct) => (
                  <option key={ct} value={ct}>
                    {ct}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleApplyFilters}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 text-sm"
            >
              Filtrovat
            </button>
            <button
              onClick={handleClearFilters}
              className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-3 rounded-md transition-colors duration-200 text-sm"
            >
              Vymazat
            </button>
          </div>
        </div>
      </div>

      {documents.length === 0 ? (
        <div className="text-gray-500 text-sm text-center py-8 dark:text-graphite-muted">
          Žádné dokumenty neodpovídají zadaným filtrům.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-graphite-border">
              <thead className="bg-gray-50 dark:bg-graphite-surface-2">
                <tr>
                  <SortableHeader column="Filename" sortBy={sortBy} sortDescending={sortDescending} onSort={handleSort}>Soubor</SortableHeader>
                  <SortableHeader column="Status" sortBy={sortBy} sortDescending={sortDescending} onSort={handleSort}>Stav</SortableHeader>
                  <SortableHeader column="IngestedAt" sortBy={sortBy} sortDescending={sortDescending} onSort={handleSort}>Vytvořeno</SortableHeader>
                  <SortableHeader column="IndexedAt" sortBy={sortBy} sortDescending={sortDescending} onSort={handleSort}>Indexováno</SortableHeader>
                  {canDelete && <th className="px-6 py-3" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-graphite-border">
                {documents.map((doc) => (
                  <tr
                    key={doc.id}
                    className={`hover:bg-gray-50 dark:hover:bg-white/5 ${doc.firstChunkId ? 'cursor-pointer' : ''}`}
                    onClick={() => doc.firstChunkId && setSelectedChunkId(doc.firstChunkId)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-graphite-text">{doc.filename}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <StatusBadge status={doc.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-graphite-muted">
                      {new Date(doc.ingestedAt).toLocaleDateString('cs-CZ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-graphite-muted">
                      {doc.indexedAt
                        ? new Date(doc.indexedAt).toLocaleDateString('cs-CZ')
                        : '–'}
                    </td>
                    {canDelete && (
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); setPendingDelete(doc); }}
                          title="Smazat dokument"
                          className="text-gray-400 hover:text-red-600 transition-colors dark:text-graphite-faint"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div className="bg-white px-3 py-2 flex items-center justify-between border-t border-gray-200 text-xs dark:bg-graphite-surface dark:border-graphite-border">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={pageNumber <= 1}
                className="relative inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-graphite-border dark:text-graphite-muted dark:bg-graphite-surface dark:hover:bg-white/5"
              >
                Předchozí
              </button>
              <button
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={pageNumber >= totalPages}
                className="ml-2 relative inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-graphite-border dark:text-graphite-muted dark:bg-graphite-surface dark:hover:bg-white/5"
              >
                Další
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div className="flex items-center space-x-3">
                <p className="text-xs text-gray-600 dark:text-graphite-muted">
                  {Math.min((pageNumber - 1) * pageSize + 1, data?.totalCount ?? 0)}–
                  {Math.min(pageNumber * pageSize, data?.totalCount ?? 0)} z {data?.totalCount ?? 0}
                </p>
                <div className="flex items-center space-x-1">
                  <span className="text-xs text-gray-600 dark:text-graphite-muted">Zobrazit:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(parseInt(e.target.value, 10));
                      setPageNumber(1);
                    }}
                    className="border border-gray-300 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
              <div>
                <nav
                  className="relative z-0 inline-flex rounded shadow-sm -space-x-px dark:shadow-soft-dark"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                    disabled={pageNumber <= 1}
                    className="relative inline-flex items-center px-1 py-1 rounded-l border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-graphite-border dark:bg-graphite-surface dark:text-graphite-muted dark:hover:bg-white/5"
                  >
                    <ChevronLeft className="h-3 w-3" />
                  </button>

                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pageNumber <= 3) {
                      pageNum = i + 1;
                    } else if (pageNumber >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = pageNumber - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPageNumber(pageNum)}
                        className={`relative inline-flex items-center px-2 py-1 border text-xs font-medium ${
                          pageNum === pageNumber
                            ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600 dark:bg-graphite-accent/10 dark:border-graphite-accent dark:text-graphite-accent'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-graphite-surface dark:border-graphite-border dark:text-graphite-muted dark:hover:bg-white/5'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                    disabled={pageNumber >= totalPages}
                    className="relative inline-flex items-center px-1 py-1 rounded-r border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-graphite-border dark:bg-graphite-surface dark:text-graphite-muted dark:hover:bg-white/5"
                  >
                    <ChevronRight className="h-3 w-3" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </>
      )}

      {pendingDelete && (
        <ConfirmDeleteDialog
          document={pendingDelete}
          error={deleteError}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}

      {selectedChunkId && (
        <LeafletChunkDetailModal
          chunkId={selectedChunkId}
          onClose={() => setSelectedChunkId(null)}
        />
      )}
    </>
  );
};

export default LeafletDocumentsTab;
